import React, { Children, ReactNode, isValidElement } from 'react';
import { usePluginContext } from '../../context/PluginContext';
import {
  ComponentPosition,
  DestinationModel,
  DestinationName,
  DestinationVariant,
  ExtendedDestinationModel
} from '../../types';
import { filterNotEmpty } from '../../../../../../utils/listHelpers';

export function useComponentsForDestination(
  name: DestinationName,
  children?: ReactNode,
  contextValue?: unknown,
  variant?: DestinationVariant
): React.ReactNode[] {
  const childrenArray = Children.toArray(children) ?? EMPTY_ARR;
  const destinationModels = usePluginContext()
    .destinationMaps.filter((p) => {
      if (variant) {
        return !!p[name] && (p[name] as ExtendedDestinationModel).variant === variant;
      }
      return !!p[name] && !(p[name] as ExtendedDestinationModel).variant;
    })
    .map((p) => p[name]);
  return mergeReactNodes(childrenArray, filterNotEmpty(destinationModels), contextValue);
}

const EMPTY_ARR: any[] = [];
const DEFAULT_POSITION: UnPartial<ComponentPosition> = { index: 0, countFrom: 'start' };

function mergeReactNodes(children: ReactNode[], destinations: DestinationModel<any>[], contextValue?: unknown) {
  const childrenArrayCopy = [...children];
  destinations.forEach((destination) => {
    const position: UnPartial<ComponentPosition> = {
      index: destination.position?.index ?? DEFAULT_POSITION.index,
      countFrom: destination.position?.countFrom ?? DEFAULT_POSITION.countFrom
    };

    const index = position.countFrom === 'end' ? childrenArrayCopy.length - position.index : position.index;
    childrenArrayCopy.splice(
      index,
      0,
      isFunction(destination.Component) ? destination.Component(contextValue) : destination.Component
    );
  });
  return childrenArrayCopy;
}

function isFunction(
  element: React.ReactNode | ((props: unknown) => React.ReactNode)
): element is (props: unknown) => React.ReactNode {
  return !Array.isArray(element) && !isValidElement(element);
}
